import { parseReleaseThemeId, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';

/**
 * The SmartLink theme that is closest to each release theme (decision D20: the two theme systems
 * stay apart; this table is the only bridge). The look of each page theme is in
 * `sml-preview/app/src/assets/styles/themes/<theme>.scss`.
 *
 * - clean (calm, light, the cover leads) → frost: daylight glass, a plain sans, soft cards.
 * - hype (loud, big words on the beat) → ember: Anton display type, a gradient call to action.
 * - dreamy (soft, slow, haze) → chill: lilac haze, a soft serif.
 * - dark / Noir (dark and quiet, a film frame) → dark: charcoal, condensed type.
 * - warm (close and golden, a small room) → velvet: a burgundy room with gold hairlines.
 * - retro (an old record sleeve) → cassette: warm card stock, fat caps.
 * - neon (tube light on black) → neon: a blackout club flyer.
 * - handmade (paper, tape, a kitchen table) → riso: two-ink print on paper.
 */
export const SMART_LINK_THEME_OF_RELEASE_THEME: Readonly<Record<ReleaseThemeId, SerializedSmartLinkVersionTheme>> = {
  clean: SerializedSmartLinkVersionTheme.frost,
  hype: SerializedSmartLinkVersionTheme.ember,
  dreamy: SerializedSmartLinkVersionTheme.chill,
  dark: SerializedSmartLinkVersionTheme.dark,
  warm: SerializedSmartLinkVersionTheme.velvet,
  retro: SerializedSmartLinkVersionTheme.cassette,
  neon: SerializedSmartLinkVersionTheme.neon,
  handmade: SerializedSmartLinkVersionTheme.riso,
};

/**
 * The SmartLink theme to preselect for a page of a song. `kit` is the song's release kit. Every new
 * kit gets the default look `clean` with no pick, so the mapped theme applies only when the artist
 * chose the release theme (`kit.lookChosen === true`; a missing field reads as not chosen). A song
 * with no kit, or with a theme that was not chosen, gives undefined: the caller keeps its own
 * default. An unknown theme id reads as the default release theme, as everywhere else.
 */
export function smartLinkThemeOfRelease(
  kit: { look: { themeId: string }; lookChosen?: boolean } | null | undefined
): SerializedSmartLinkVersionTheme | undefined {
  if (kit === null || kit === undefined || kit.lookChosen !== true) {
    return undefined;
  }
  return SMART_LINK_THEME_OF_RELEASE_THEME[parseReleaseThemeId(kit.look.themeId)];
}
